import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHome, FaCalendar, FaUser, FaBell, FaCog, FaSignOutAlt, FaBars, FaTimes, FaArrowLeft, FaDollarSign, FaStar, FaExclamationTriangle, FaInfoCircle, FaEnvelope } from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { AUTH_TOKEN_KEY } from '../../utils/constants';
import { toast } from 'react-toastify';
import './DashboardLayout.css';

const DashboardLayout = ({ children, title, showBackButton = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { icon: FaHome, label: 'Dashboard', path: '/dashboard' },
    { icon: FaCalendar, label: 'Upcoming Appointments', path: '/appointments' },
    { icon: FaCalendar, label: 'Past Appointments', path: '/past-appointments' },
    { icon: FaUser, label: 'Profile', path: '/profile' },
    { icon: FaDollarSign, label: 'Revenue', path: '/earnings' },
    { icon: FaStar, label: 'Ratings', path: '/reviews' },
    { icon: FaCog, label: 'Settings', path: '/complete-profile' },
  ];

  const supportItems = [
    { icon: FaInfoCircle, label: 'About Us', path: '/about-us' },
    { icon: FaEnvelope, label: 'Contact Us', path: '/contact' },
  ];

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(null);
    navigate('/login');
    toast.success('Logged out successfully');
    setShowLogoutConfirm(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside className="desktop-sidebar">
        <div className="sidebar-header" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
          <img src={require('../../assets/images/logo.jpg')} alt="MeetUrExpert for Professionals" className="sidebar-logo" />
          <div className="sidebar-logo-text">
            <h1>MeetUrExpert</h1>
            <span>For Professionals</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <item.icon />
              <span>{item.label}</span>
            </button>
          ))}
          
          <div className="sidebar-divider"></div>
          
          {supportItems.map((item) => (
            <button
              key={item.path}
              className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <item.icon />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.profilePicture?.secure_url ? (
                <img src={user.profilePicture.secure_url} alt="Profile" />
              ) : (
                <div className="default-avatar">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              )}
            </div>
            <div className="user-details">
              <p className="user-name">{user?.name}</p>
              <p className="user-email">{user?.email}</p>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogoutClick}>
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      
      {/* Mobile Sidebar */}
      <aside className={`mobile-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="mobile-sidebar-header">
          <div className="mobile-logo-container" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
            <img src={require('../../assets/images/logo.jpg')} alt="MeetUrExpert for Professionals" className="sidebar-logo" />
            <div className="mobile-logo-text">
              <h1>MeetUrExpert</h1>
              <span>For Professionals</span>
            </div>
          </div>
          <button className="close-sidebar" onClick={() => setSidebarOpen(false)}>
            <FaTimes />
          </button>
        </div>
        
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
            >
              <item.icon />
              <span>{item.label}</span>
            </button>
          ))}
          
          <div className="sidebar-divider"></div>
          
          {supportItems.map((item) => (
            <button
              key={item.path}
              className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
            >
              <item.icon />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogoutClick}>
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Header */}
        <header className="top-header">
          <div className="header-left">
            {!showBackButton && (
              <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>
                <FaBars />
              </button>
            )}
            {showBackButton && (
              <button className="back-btn-dashboard-layout" onClick={handleBack}>
                <FaArrowLeft />
              </button>
            )}
            {title && <h1 className="page-title">{title}</h1>}
          </div>
          
          <div className="header-right">
            <button className="notification-btn" onClick={() => navigate('/notifications')}>
              <FaBell />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        <nav className="mobile-bottom-nav">
          <button
            className={`nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
            onClick={() => navigate('/dashboard')}
          >
            <FaHome />
            <span>Home</span>
          </button>
          <button
            className={`nav-item ${location.pathname === '/appointments' ? 'active' : ''}`}
            onClick={() => navigate('/appointments')}
          >
            <FaCalendar />
            <span>Appointments</span>
          </button>
          <button
            className={`nav-item ${location.pathname === '/profile' ? 'active' : ''}`}
            onClick={() => navigate('/profile')}
          >
            <FaUser />
            <span>Profile</span>
          </button>
        </nav>
      </div>

      {/* Logout Confirmation Popup */}
      {showLogoutConfirm && (
        <div className="logout-popup-overlay">
          <div className="logout-popup">
            <div className="logout-popup-header">
              <div className="logout-icon">
                <FaExclamationTriangle />
              </div>
            </div>
            <div className="logout-popup-content">
              <h3>Confirm Logout</h3>
              <p>Are you sure you want to logout from your account?</p>
              <div className="logout-popup-actions">
                <button className="logout-cancel-btn" onClick={cancelLogout}>
                  Cancel
                </button>
                <button className="logout-confirm-btn" onClick={confirmLogout}>
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;